import type { Theme } from "@mui/material";

export interface HighlightSegment {
  text: string;
  isMatch: boolean;
}

/**
 * Split text into segments, marking parts that match the search query.
 * Case-insensitive matching.
 */
export function getHighlightSegments(
  text: string,
  query: string,
): HighlightSegment[] {
  if (!query || query.length === 0) {
    return [{ text, isMatch: false }];
  }

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "gi");
  const parts = text.split(regex);

  return parts
    .filter((part) => part.length > 0)
    .map((part) => ({
      text: part,
      isMatch: part.toLowerCase() === query.toLowerCase(),
    }));
}

const CONTEXT_CHARS = 50;

/**
 * Extract a snippet around the first match: up to 50 chars before,
 * the match, and up to 50 chars after. Adds "..." when truncated.
 * Returns highlight segments for the snippet.
 */
export function getSnippetSegments(
  text: string,
  query: string,
): HighlightSegment[] {
  if (!query || query.length === 0) {
    return [{ text, isMatch: false }];
  }

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(escapedQuery, "gi");
  const match = regex.exec(text);

  if (!match) {
    return [{ text, isMatch: false }];
  }

  const matchStart = match.index;
  const matchEnd = matchStart + match[0].length;

  const snippetStart = Math.max(0, matchStart - CONTEXT_CHARS);
  const snippetEnd = Math.min(text.length, matchEnd + CONTEXT_CHARS);

  const before = text.slice(snippetStart, matchStart);
  const matched = text.slice(matchStart, matchEnd);
  const after = text.slice(matchEnd, snippetEnd);

  const segments: HighlightSegment[] = [];

  const prefixEllipsis = snippetStart > 0 ? "..." : "";
  const suffixEllipsis = snippetEnd < text.length ? "..." : "";

  if (prefixEllipsis || before) {
    segments.push({ text: prefixEllipsis + before, isMatch: false });
  }
  segments.push({ text: matched, isMatch: true });
  if (after || suffixEllipsis) {
    segments.push({ text: after + suffixEllipsis, isMatch: false });
  }

  return segments;
}

/**
 * Returns the sx styles for a highlighted text span.
 */
export function getHighlightSx(theme: Theme) {
  return {
    backgroundColor: theme.palette.primitive.system.indigo[100],
    color: theme.palette.lightBg.medium,
    padding: "0 2px",
    borderRadius: "2px",
  } as const;
}
